var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { AwsUi } from '../../../core/runtime/src';
import { Icon } from '../../icon/src';
import { fitFlyout } from './utils/dropdown-fit-handler';
export var getHref = function (item) {
    return item.href;
};
var isCategoryDisabled = function (category) {
    return category.disabled || (category.items.length > 0 && category.items.every(function (item) { return item.disabled; }));
};
var ItemElement = function (_a) {
    var item = _a.item, isDisabled = _a.isDisabled, prefixClass = _a.prefixClass, activateItem = _a.activateItem, isSelected = _a.isSelected, hasBottomBorder = _a.hasBottomBorder;
    var href = getHref(item);
    var className = [
        prefixClass('item'),
        href && prefixClass('item-link'),
        isSelected && prefixClass('item-highlighted'),
        isDisabled && prefixClass('item-disabled'),
        hasBottomBorder && prefixClass('item-has-bottom-border')
    ]
        .filter(function (className) { return className; })
        .join(' ');
    var attributes = {
        className: className,
        role: 'menuitem',
        'data-testid': item.id,
        'data-description': item.description,
        'aria-disabled': isDisabled ? 'true' : undefined,
        // We are using the roving tabindex technique to manage the focus state of the dropdown.
        // The current element will always have tabindex=0 which means that it can be tabbed to,
        // while all other items have tabindex=-1 so we can focus them when necessary.
        tabindex: isDisabled ? undefined : isSelected ? 0 : -1
    };
    var onClick = function (event) {
        !href && event.preventDefault();
        !isDisabled && activateItem(item, event);
    };
    return !href ? (AwsUi.__createElement("li", __assign({}, attributes, { onclick: onClick }), item.text)) : (AwsUi.__createElement("li", __assign({}, attributes),
        AwsUi.__createElement("a", { href: href, onclick: onClick }, item.text)));
};
var ExpandableCategoryElement = function (_a) {
    var item = _a.item, prefixClass = _a.prefixClass, createItems = _a.createItems, isExpanded = _a.isExpanded, isSelected = _a.isSelected, activateItem = _a.activateItem, isMobile = _a.isMobile;
    var categoryDisabled = isCategoryDisabled(item);
    var className = [
        prefixClass('category'),
        prefixClass('category-expandable'),
        isExpanded && isMobile && prefixClass('category-expanded'),
        categoryDisabled && prefixClass('category-disabled'),
        (isSelected || (isExpanded && !isMobile)) && prefixClass('category-highlighted')
    ]
        .filter(function (className) { return className; })
        .join(' ');
    var attributes = {
        className: className,
        'aria-disabled': categoryDisabled ? 'true' : undefined
    };
    var groupMounted = function (flyout) {
        var parentElement = flyout.parentElement;
        if (parentElement) {
            fitFlyout(flyout, parentElement);
        }
    };
    var groupProps = __assign(__assign({}, (isExpanded && !isMobile
        ? {
            componentDidMount: groupMounted,
            lifecycleKey: 'expanded-group'
        }
        : {})), { className: [prefixClass('items'), !isMobile && prefixClass('items-fly-out')]
            .filter(function (className) { return className; })
            .join(' ') });
    // Hide the category title element from screen read/ers because it will be
    // provided as an ARIA label.
    return (AwsUi.__createElement("li", __assign({}, attributes),
        item.text && (AwsUi.__createElement("p", { onclick: function (e) {
                if (!categoryDisabled) {
                    e.preventDefault();
                    activateItem(item);
                }
            }, className: prefixClass('category-header'), "data-testid": item.id, 
            // We are using the roving tabindex technique to manage the focus state of the dropdown.
            // The current element will always have tabindex=0 which means that it can be tabbed to,
            // while all other items have tabindex=-1 so we can focus them when necessary.
            tabindex: item.disabled ? undefined : isSelected ? 0 : -1 },
            item.text,
            AwsUi.__createElement("span", { className: prefixClass('expand-icon') +
                    ' ' +
                    prefixClass('expand-icon-' + (!isMobile ? 'right' : isExpanded ? 'up' : 'down')) },
                AwsUi.__createElement(Icon, { name: "caret-down-filled" })))),
        isExpanded ? (AwsUi.__createElement("ul", __assign({}, groupProps, { role: "group", "aria-label": item.text }), createItems(item.items, categoryDisabled))) : null));
};
var CategoryElement = function (_a) {
    var item = _a.item, prefixClass = _a.prefixClass, createItems = _a.createItems;
    var categoryDisabled = isCategoryDisabled(item);
    var className = [prefixClass('category'), categoryDisabled && prefixClass('category-disabled')]
        .filter(function (className) { return className; })
        .join(' ');
    var attributes = {
        className: className,
        role: 'presentation',
        'aria-disabled': categoryDisabled ? 'true' : undefined
    };
    // Hide the category title element from screen readers because it will be
    // provided as an ARIA label.
    return (AwsUi.__createElement("li", __assign({}, attributes),
        item.text && (AwsUi.__createElement("p", { className: prefixClass('category-header'), "aria-hidden": "true" }, item.text)),
        AwsUi.__createElement("ul", { className: prefixClass('items'), role: "group", "aria-label": item.text }, createItems(item.items, categoryDisabled))));
};
export var DropdownItemsCreator = function (_a) {
    var getExpandedItem = _a.getExpandedItem, getHighlightedItem = _a.getHighlightedItem, getExpandableGroups = _a.getExpandableGroups, activateItem = _a.activateItem, prefixClass = _a.prefixClass, isMobile = _a.isMobile;
    var createItems = function (items, categoryDisabled) {
        if (categoryDisabled === void 0) { categoryDisabled = false; }
        return items.map(function (item, index) {
            var props = {
                item: item,
                prefixClass: prefixClass
            };
            var expandableItems = getExpandableGroups();
            if (item.items) {
                var categoryProps = __assign(__assign({}, props), { createItems: createItems });
                if (expandableItems) {
                    var expandableCategoryProps = __assign(__assign({}, categoryProps), { isExpanded: item === getExpandedItem(), isSelected: item === getHighlightedItem(), isMobile: isMobile(), activateItem: activateItem });
                    return item.text ? AwsUi.__createElement(ExpandableCategoryElement, __assign({}, expandableCategoryProps)) : null;
                }
                return AwsUi.__createElement(CategoryElement, __assign({}, categoryProps));
            }
            var itemProps = __assign(__assign({}, props), { activateItem: activateItem, isDisabled: item.disabled || categoryDisabled, isSelected: item === getHighlightedItem(), hasBottomBorder: index === items.length - 1 || !!items[index + 1].items });
            return AwsUi.__createElement(ItemElement, __assign({}, itemProps));
        });
    };
    return createItems;
};
//# sourceMappingURL=dropdown-items.js.map